import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, CheckCircle2, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function VendorRegister() {
  const { user, register } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", business_name: "", gstin: "", category: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (user && user !== false) {
        navigate(user.role === "vendor" ? "/vendor/onboarding" : "/account");
        return;
      }
      await register({ ...form, role: "vendor" });
      toast.success("Vendor account created! Welcome aboard.");
      navigate("/vendor/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-[#1F1610] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <span className="eyebrow text-primary">Become a Seller</span>
          <h1 className="mt-3 text-4xl font-serif font-bold max-w-xl">Sell on Shri Vrindavan Shop</h1>
          <p className="mt-3 text-stone-300 max-w-xl">Reach devotees across India. List your spiritual, pooja and Braj products with B2C & B2B wholesale support.</p>
          <div className="mt-6 flex flex-wrap gap-6">
            {[[TrendingUp, "Grow sales pan-India"], [Users, "Reach lakhs of devotees"], [ShieldCheck, "Verified seller trust badge"]].map(([Icon, t]) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm text-stone-200"><Icon size={17} className="text-primary" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-2 text-secondary mb-2"><Store size={22} /><span className="eyebrow">Vendor Registration</span></div>
        <h2 className="text-2xl font-serif font-bold text-stone-900">Create your seller account</h2>
        <p className="mt-1 text-sm text-stone-500">After registration you will complete business details, KYC documents and bank information for verification.</p>

        {error && <div data-testid="vendor-register-error" className="mt-4 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2.5">{error}</div>}

        <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="vendor-register-form">
          {!user || user === false ? (
            <>
              <Field k="name" label="Contact Name" set={set} form={form} req />
              <Field k="email" label="Email" type="email" set={set} form={form} req />
              <Field k="password" label="Password" type="password" set={set} form={form} req />
              <Field k="phone" label="Phone" set={set} form={form} />
            </>
          ) : (
            <div className="sm:col-span-2 rounded-lg bg-accent/60 p-3 text-sm text-stone-700">Applying as <b>{user.name}</b> ({user.email})</div>
          )}
          <Field k="business_name" label="Business / Store Name" set={set} form={form} req />
          <Field k="gstin" label="GSTIN (optional)" set={set} form={form} />
          <Field k="category" label="Primary Category" set={set} form={form} req className="sm:col-span-2" />
          <button disabled={loading} data-testid="vendor-register-submit" className="sm:col-span-2 rounded-full bg-secondary text-white font-semibold py-3 hover:bg-[#5C061C]">
            {loading ? "Submitting..." : "Register as Vendor"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ k, label, type = "text", set, form, req, className = "" }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-stone-600">{label}{req && " *"}</span>
      <input required={req} type={type} value={form[k]} onChange={set(k)} data-testid={`vendor-field-${k}`} className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
    </label>
  );
}
